import {
  ChartBarIcon as ChartBarIconOutline,
  TableIcon as TableIconOutline,
} from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarIconSolid,
  TableIcon as TableIconSolid,
} from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ANALYTICS_TAB_INDEX, REQUESTS_TAB_INDEX } from "@/components/RequestsAnalytics/constants";
import { getAnalyticsDisabledTooltip } from "@/components/RequestsAnalytics/utils/getAnalyticsDisabledTooltip";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import TabNavigation from "@/components/ui/TabNavigation";
import { useUser } from "@/context/user-context";
import { isOlderDataSearch } from "@/routes/traces/search/older-data";

const TraceAnalyticsContentLazy = lazy(() => import("./TraceAnalyticsContent"));
const TracesAgGridLazy = lazy(() => import("./TracesAgGrid"));

const tabPanelSuspenseFallback = (
  <div className="flex h-full min-h-[200px] items-center justify-center">
    <LoadingSpinner />
  </div>
);

const TABLE_TAB_INDEX = REQUESTS_TAB_INDEX;

const TracesAnalyticsTabs = observer(
  ({
    selectedTabIndex,
    syncUrl = true,
    onSelectedTabChange,
  }: {
    selectedTabIndex: number;
    syncUrl?: boolean;
    onSelectedTabChange?: (tabIndex: number) => void;
  }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const { activeWorkspace } = useUser();
    const { workspaceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [localSelectedTabIndex, setLocalSelectedTabIndex] = useState(selectedTabIndex);
    const isAnalyticsDisabledForOlderData =
      !!activeWorkspace &&
      isOlderDataSearch(
        structuredSearchStore.dateRange,
        activeWorkspace.traces_search_cutover_date,
      );
    const requestedSelectedTabIndex = syncUrl ? selectedTabIndex : localSelectedTabIndex;
    const currentSelectedTabIndex =
      isAnalyticsDisabledForOlderData && requestedSelectedTabIndex === ANALYTICS_TAB_INDEX
        ? TABLE_TAB_INDEX
        : requestedSelectedTabIndex;
    const analyticsDisabledTooltip = isAnalyticsDisabledForOlderData
      ? getAnalyticsDisabledTooltip(activeWorkspace?.traces_search_cutover_date)
      : undefined;

    const getTabPath = useCallback(
      (tabIndex: number) => {
        if (!syncUrl) {
          return undefined;
        }

        const route = tabIndex === ANALYTICS_TAB_INDEX ? "traces-analytics" : "traces-search";
        return `/workspace/${workspaceId}/${route}${location.search}`;
      },
      [location.search, syncUrl, workspaceId],
    );

    useEffect(() => {
      if (selectedTabIndex === ANALYTICS_TAB_INDEX && isAnalyticsDisabledForOlderData) {
        if (!syncUrl) {
          setLocalSelectedTabIndex(TABLE_TAB_INDEX);
          onSelectedTabChange?.(TABLE_TAB_INDEX);
          return;
        }

        const tracesSearchPath = getTabPath(TABLE_TAB_INDEX);
        if (tracesSearchPath) {
          navigate(tracesSearchPath, { replace: true });
        }
      }
    }, [
      getTabPath,
      isAnalyticsDisabledForOlderData,
      navigate,
      onSelectedTabChange,
      selectedTabIndex,
      syncUrl,
    ]);

    useEffect(() => {
      setLocalSelectedTabIndex(selectedTabIndex);
    }, [selectedTabIndex]);

    const handleTabChange = (tabIndex: number) => {
      if (!syncUrl) {
        setLocalSelectedTabIndex(tabIndex);
        onSelectedTabChange?.(tabIndex);
      }
    };

    return (
      <div className="min-h-0 flex-1">
        <TabNavigation
          selectedIndex={currentSelectedTabIndex}
          handleTabChange={handleTabChange}
          panelClassName="min-h-0"
          panelContainerClassName="mt-4 min-h-0"
          tabs={[
            {
              id: TABLE_TAB_INDEX,
              name: "Traces",
              path: getTabPath(TABLE_TAB_INDEX),
              outlineIcon: <TableIconOutline className="h-4 w-4" />,
              solidIcon: <TableIconSolid className="h-4 w-4" />,
              content:
                currentSelectedTabIndex === TABLE_TAB_INDEX ? (
                  <Suspense fallback={tabPanelSuspenseFallback}>
                    <TracesAgGridLazy />
                  </Suspense>
                ) : null,
            },
            {
              id: ANALYTICS_TAB_INDEX,
              name: "Analytics",
              path: getTabPath(ANALYTICS_TAB_INDEX),
              outlineIcon: <ChartBarIconOutline className="h-4 w-4" />,
              solidIcon: <ChartBarIconSolid className="h-4 w-4" />,
              content:
                currentSelectedTabIndex === ANALYTICS_TAB_INDEX ? (
                  <Suspense fallback={tabPanelSuspenseFallback}>
                    <TraceAnalyticsContentLazy />
                  </Suspense>
                ) : null,
              disabled: isAnalyticsDisabledForOlderData,
              tooltipContent: analyticsDisabledTooltip,
            },
          ]}
        />
      </div>
    );
  },
);

export default TracesAnalyticsTabs;
